/**
 * ClaimSync Homepage
 * Modern split-layout design with stats cards
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Zap, FileText, BarChart3, Lock, Target, TrendingUp, Users, Share2, Database, LineChart } from 'lucide-react';

export default function HomePage() {
  useEffect(() => {
    // Add scroll effect to header
    const header = document.querySelector('header');
    const handleScroll = () => {
      if (header) {
        if (window.pageYOffset > 100) {
          header.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
        } else {
          header.style.boxShadow = 'none';
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628] border-b border-white/10">
        <nav className="max-w-[1400px] mx-auto px-8 lg:px-12 py-5 flex justify-between items-center">
          <Link to="/home" className="flex items-center">
            <img src="/logo.svg" alt="ClaimSync" className="h-44 lg:h-56 w-auto" />
          </Link>
          <ul className="hidden md:flex items-center gap-8 lg:gap-10">
            <li><a href="#features" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Features</a></li>
            <li><Link to="/about" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">About</Link></li>
            <li><Link to="/contact" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Contact</Link></li>
            <li><Link to="/privacy" className="text-white/85 hover:text-white font-medium text-sm tracking-tight transition-colors">Privacy</Link></li>
            <li>
              <Link 
                to="/sign-in" 
                className="bg-[#2762EA] hover:bg-[#1e50c7] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
              >
                Sign In
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-56 lg:pt-64 pb-16 lg:pb-24 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute -top-72 -right-48 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-36 -left-24 w-96 h-96 bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-24 items-center">
            {/* Left - Text */}
            <div className="animate-fadeInUp">
              <span className="inline-block text-[#2762EA] font-semibold text-sm uppercase tracking-wider mb-6">
                ClaimSync Engine
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-[#0A1628] leading-[1.1] mb-6 tracking-tight">
                Business Interruption Calculations in Minutes, Not Weeks
              </h1>
              <p className="text-lg lg:text-xl text-slate-600 mb-10 leading-relaxed max-w-xl">
                ClaimSync automates complex business interruption calculations with AI-powered financial analysis, 
                reducing weeks of forensic accounting work to minutes while maintaining precision and compliance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/waitlist" 
                  className="inline-flex items-center justify-center gap-2 bg-[#2762EA] hover:bg-[#1e50c7] text-white px-7 py-3.5 rounded-lg font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20"
                >
                  Get Started <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/demo" 
                  className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#0A1628] text-[#0A1628] px-7 py-3.5 rounded-lg font-semibold transition-all hover:bg-[#0A1628] hover:text-white hover:-translate-y-0.5"
                >
                  View Demo
                </Link>
              </div>
            </div>

            {/* Right - Stats Card */}
            <div className="animate-fadeInRight">
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 lg:p-10 border border-blue-100/50 shadow-xl shadow-slate-200/50">
                <div className="space-y-6">
                  <StatCard 
                    label="Time Savings" 
                    value="87%" 
                    description="Average reduction in processing time" 
                  />
                  <StatCard 
                    label="Accuracy Rate" 
                    value="99.4%" 
                    description="AI-verified calculations" 
                  />
                  <StatCard 
                    label="Time Savings" 
                    value="Hours→Min" 
                    description="From days of work to minutes" 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-28 bg-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4 leading-tight tracking-tight">
              Built for Complex Claims, Designed for Simplicity
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Enterprise-grade automation that handles the full spectrum of business interruption calculations with unprecedented speed and accuracy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard 
              icon={<Zap className="w-7 h-7 text-[#2762EA]" />}
              title="AI-Powered Analysis"
              description="Advanced machine learning algorithms process bank statements, receipts, and financial documents with human-level accuracy in seconds."
            />
            <FeatureCard 
              icon={<BarChart3 className="w-7 h-7 text-[#2762EA]" />}
              title="Automated Workflows"
              description="From client onboarding to final PDF report, our step-by-step process ensures nothing is missed. Built-in validation catches errors before they become problems."
            />
            <FeatureCard 
              icon={<TrendingUp className="w-7 h-7 text-[#2762EA]" />}
              title="Monte Carlo Simulation"
              description="Run 10,000+ scenarios to generate claim range projections with statistical confidence intervals. Know your best and worst case outcomes."
            />
            <FeatureCard 
              icon={<Users className="w-7 h-7 text-[#2762EA]" />}
              title="Team Collaboration"
              description="Role-based access control with Owner, Admin, Analyst, and Viewer roles. Manage your team, track activity, and collaborate securely."
            />
            <FeatureCard 
              icon={<FileText className="w-7 h-7 text-[#2762EA]" />}
              title="Comprehensive Reporting"
              description="Generate court-ready financial reports with detailed breakdowns, trend analysis, and supporting documentation automatically."
            />
            <FeatureCard 
              icon={<Lock className="w-7 h-7 text-[#2762EA]" />}
              title="Bank-Level Security"
              description="End-to-end encryption and role-based access control protect your sensitive financial data."
            />
            <FeatureCard 
              icon={<Shield className="w-7 h-7 text-[#2762EA]" />}
              title="Real-Time Processing"
              description="Multi-page document OCR, anomaly detection, and automated data validation happen in real-time as files are uploaded."
            />
            <FeatureCard 
              icon={<Target className="w-7 h-7 text-[#2762EA]" />}
              title="Disaster-Specific Models"
              description="Pre-configured calculation engines for hurricanes, wildfires, pandemics, and other business interruption scenarios."
            />
          </div>
        </div>
      </section>

      {/* More Features Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4 leading-tight tracking-tight">
              And There's More
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Additional tools and integrations to streamline every aspect of your claims workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MoreFeatureCard 
              icon={<LineChart className="w-5 h-5 text-[#2762EA]" />}
              title="Analytics Dashboard"
              description="Track claim trends, team performance, and financial summaries at a glance."
            />
            <MoreFeatureCard 
              icon={<Database className="w-5 h-5 text-[#2762EA]" />}
              title="BLS Industry Benchmarks"
              description="Live Bureau of Labor Statistics data for accurate industry comparisons."
            />
            <MoreFeatureCard 
              icon={<Share2 className="w-5 h-5 text-[#2762EA]" />}
              title="Public Report Sharing"
              description="Generate shareable links for clients without requiring them to log in."
            />
            <MoreFeatureCard 
              icon={<FileText className="w-5 h-5 text-[#2762EA]" />}
              title="Excel/CSV Export"
              description="Export your data and reports in multiple formats for flexibility."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2762EA]">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
            Ready to Transform Your Claims Process?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Join professionals using ClaimSync to calculate business interruption claims faster and more accurately.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/waitlist" 
              className="inline-flex items-center justify-center gap-2 bg-white text-[#2762EA] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all hover:-translate-y-0.5"
            >
              Get Started Today <ArrowRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] py-12">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <img src="/logo.svg" alt="ClaimSync" className="h-10 w-auto mb-4" />
              <p className="text-slate-400 text-sm">
                Professional business interruption claim calculations for accountants and legal professionals.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/demo" className="text-slate-400 hover:text-white text-sm transition-colors">Demo</Link></li>
                <li><a href="#features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} ClaimSync. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Custom Styles */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out;
        }
        .animate-fadeInRight {
          animation: fadeInRight 1s ease-out 0.2s both;
        }
      `}</style>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, description }: { label: string; value: string; description: string }) {
  return (
    <div className="bg-white p-6 rounded-xl border border-blue-100/50 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300">
      <div className="text-slate-500 font-semibold text-xs uppercase tracking-wider mb-2">{label}</div>
      <div className="text-4xl font-bold text-[#2762EA] leading-none mb-2">{value}</div>
      <div className="text-slate-600 text-sm">{description}</div>
    </div>
  );
}

// Feature Card Component
function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white p-8 rounded-2xl border border-slate-200/50 hover:-translate-y-2 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 relative overflow-hidden group">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2762EA] to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
      <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-[#0A1628] mb-3">{title}</h3>
      <p className="text-slate-600 leading-relaxed">{description}</p>
    </div>
  );
}

// More Feature Card Component (smaller, simpler)
function MoreFeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-[#FAFAF9] p-6 rounded-xl border border-slate-200/50 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/50 transition-all duration-300 group">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
          {icon}
        </div>
        <h3 className="font-bold text-[#0A1628]">{title}</h3>
      </div>
      <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
    </div>
  );
}
